// Performance.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; // Date picker adapter
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; // Date picker provider
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Date picker component
// Removed duplicate axios import
import {
  Container,
  Box,
  Paper,
  Grid,
  Card, // Added Card
  CardContent, // Added CardContent
  CircularProgress,
  Snackbar,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Tooltip,
  TextField,
  useMediaQuery,
  Typography,
  Stack,
  Button,
  Avatar,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  StarRate as StarRateIcon,
  TaskAlt as TaskAltIcon,
  ThumbUpAlt as ThumbUpAltIcon,
  GpsFixed as GpsFixedIcon,
  PhotoCamera as PhotoCameraIcon,
  WorkHistory as WorkHistoryIcon,
  Paid as PaidIcon,
  Schedule as ScheduleIcon,
  SportsScore as SportsScoreIcon,
  SmsOutlined as SmsOutlinedIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import dayjs from 'dayjs'; // Already imported, ensure it's used
import SideMenu from '../components/SideMenu'; // <-- Import SideMenu
import { useUser } from '../context/UserContext'; // Import UserContext
import { usePageTour } from '../tours/usePageTour';
import { usePermissions } from '../context/PermissionContext'; // Import PermissionContext

// Chart.js 4 registers nothing by default; without the arc element a <Pie> throws
// while mounting, which unmounts the whole app ("React App Debug Info" page).
ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

/**
 * Colorful stat card used across the Performance page.
 * - percent metrics get a color-coded progress bar (green/amber/red)
 * - `extra` renders a small caption row under the value (e.g. counts)
 */
const MetricCard = ({ title, tooltip, icon, value, percent = null, color = 'primary.main', extra = null }) => {
  const barColor = percent === null ? 'primary'
    : percent >= 90 ? 'success'
    : percent >= 70 ? 'warning'
    : 'error';
  return (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        borderTop: 4,
        borderColor: color,
        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" mb={1}>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
          <Tooltip title={tooltip || ''}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {title} <InfoIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: 'text.disabled' }} />
            </Typography>
          </Tooltip>
        </Stack>
        <Typography variant="h3" sx={{ fontWeight: 700 }} aria-label={title}>
          {value}
        </Typography>
        {percent !== null && (
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, percent))}
            color={barColor}
            sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
          />
        )}
        {extra && (
          <Box sx={{ mt: 1.5 }}>
            {extra}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

function Performance() {
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  // State variables
  const [metrics, setMetrics] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driversLoading, setDriversLoading] = useState(false); // New state for driver list loading
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [reviewFilter, setReviewFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  // const [locations, setLocations] = useState([]); // REMOVED - Replaced by facilitiesList
  // const [selectedLocations, setSelectedLocations] = useState([]); // REMOVED - Replaced by selectedFacility
  const [facilitiesList, setFacilitiesList] = useState([]); // List of available facilities (SPs)
  const [filteredFacilitiesList, setFilteredFacilitiesList] = useState([]); // Filtered facilities list
  const [selectedFacility, setSelectedFacility] = useState(''); // Currently selected facility vendorNumber
  const [driversList, setDriversList] = useState([]); // List of available drivers (for Org 1 users)
  const [selectedDriver, setSelectedDriver] = useState(''); // Currently selected driver ID
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'days')); // Default start date (last 30 days)
  const [endDate, setEndDate] = useState(dayjs()); // Default end date (today)
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false); // Whether to show the filters panel
  const [companyNameFilter, setCompanyNameFilter] = useState('');
  const [vendorIdFilter, setVendorIdFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [zipFilter, setZipFilter] = useState('');
  
  // Get current user from context
  const { currentUser } = useUser();
  const { hasPermission } = usePermissions();
  usePageTour('performance', !!currentUser); // first-visit tour of this page

  // Determine if user is Org 1
  const isOrg1User = currentUser?.organizationLevel === 1;
  
  // Determine if user has admin or dispatcher secondary role
  const hasAdminRole = currentUser?.secondaryRoles?.admin;
  const hasDispatcherRole = currentUser?.secondaryRoles?.dispatcher;
  const hasAdminOrDispatcherRole = hasAdminRole || hasDispatcherRole;
  
  // Determine if user is a driver-only Org 1 user
  const isDriverOnly = isOrg1User && !hasAdminOrDispatcherRole && currentUser?.primaryRole === 'N/A' &&
                      currentUser?.secondaryRoles?.driver;
  
  // Determine if facility dropdown should be shown
  const showFacilityDropdown = !isOrg1User || (isOrg1User && hasAdminOrDispatcherRole);
  
  // Determine if driver dropdown should be shown
  const showDriverDropdown = isOrg1User;

  // REMOVED Sample data

  // Fetch facilities (SPs) on component mount
  useEffect(() => {
    const fetchFacilities = async () => {
      setLoading(true); // Use main loading state for initial fetch
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        // Fetch users with primaryRole SP
        // Assuming the controller handles the query param
        // Corrected API path with /v1/ prefix
        // Fetch actual facilities
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities`, config); // Use facilities endpoint
        // Filter for active facilities and map to { _id, facilityName, vendorNumber }
        const facilityList = data.records // Access the 'records' array
          .filter(facility => facility.status === 'Active') // Ensure only active facilities are listed
          .map(facility => ({
            _id: facility._id, // Use facility's unique ID
            facilityName: facility.facilityName || `Facility ${facility._id}`, // Use facilityName
            vendorNumber: facility.vendorNumber, // Keep vendorNumber for display if needed
          }));

        setFacilitiesList(facilityList); // Use the mapped facilityList
        setFilteredFacilitiesList(facilityList); // Initialize filtered list with all facilities
        // Removed duplicate setFacilitiesList call
        // Optionally select the first facility by default
        if (facilityList.length > 0) {
          setSelectedFacility(facilityList[0]._id); // Select the facility ID
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
        setSnackbarMessage('Failed to load facilities list.');
        setSnackbarOpen(true);
      } finally {
        // We will set loading to false after performance data is fetched
        // setLoading(false);
      }
    };

    fetchFacilities();
  }, []); // Run only on mount

// Fetch drivers list for Org 1 users
useEffect(() => {
  // Log when effect runs and why
  console.log('[Performance] fetchDrivers effect triggered. Dependencies:', {
    isOrg1User,
    isDriverOnly,
    currentUser: currentUser?._id,
    selectedFacility,
    facilitiesList: facilitiesList.length
  });

  // Only fetch drivers if user is Org 1
  if (!isOrg1User) {
    if (driversList.length > 0) setDriversList([]);
    if (selectedDriver !== '') setSelectedDriver('');
    return;
  }

  const fetchDrivers = async () => {
    setDriversLoading(true); // Use driversLoading for this fetch
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const selectedFacilityObject = selectedFacility ? facilitiesList.find(f => f._id === selectedFacility) : null;
      const selectedFacilityVendorNumber = selectedFacilityObject?.vendorNumber;

      // Fetch drivers based on the selected facility's vendor number
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
        ...config,
        params: {
          organizationLevel: 1,
          hasDriverRole: true,
          vendorNumber: selectedFacilityVendorNumber,
        },
      });

      // After fetching data
      console.log('[Performance] Fetched drivers response:', {
        data,
        selectedFacility,
        selectedFacilityObject
      });

      // Filter and map drivers
      let driverList = data.records
        .filter(user => (user.secondaryRoles?.driver || user.primaryRole === 'DR'))
        .map(user => ({
          _id: user._id,
          name: `${user.firstName} ${user.lastName}`,
          vendorNumber: user.vendorNumber || user.vendorId,
        }));
        
      if (isDriverOnly) {
        driverList = driverList.filter(driver => driver._id === currentUser._id);
      }

      // After mapping/filtering drivers
      console.log('[Performance] Processed drivers:', {
        originalList: data.records,
        filteredList: driverList,
        isDriverOnly,
        hasAdminOrDispatcherRole,
        currentUserId: currentUser?._id
      });

      // Before setting driversList
      console.log('[Performance] Setting driversList:', driverList);
      setDriversList(driverList);

      // Before setting selectedDriver - log the decision factors
      console.log('[Performance] Deciding selectedDriver:', {
        isDriverOnly,
        hasAdminOrDispatcherRole,
        currentUserId: currentUser?._id,
        currentUserInList: driverList.some(d => d._id === currentUser?._id),
        driverListLength: driverList.length
      });

      if (isDriverOnly) {
        if (driverList.some(driver => driver._id === currentUser._id)) {
          console.log('[Performance] Setting selectedDriver to currentUser:', currentUser._id);
          setSelectedDriver(currentUser._id);
        } else {
          console.log('[Performance] Setting selectedDriver to empty (driver not in list)');
          setSelectedDriver('');
        }
      } else if (hasAdminOrDispatcherRole) {
        console.log('[Performance] Setting selectedDriver to empty (admin/dispatcher)');
        setSelectedDriver('');
      } else {
        if (driverList.length > 0) {
          console.log('[Performance] Setting selectedDriver to first driver:', driverList[0]._id);
          setSelectedDriver(driverList[0]._id);
        } else {
          console.log('[Performance] Setting selectedDriver to empty (no drivers)');
          setSelectedDriver('');
        }
      }
    } catch (error) {
      console.error('[Performance] Error fetching drivers:', error);
      setSnackbarMessage('Failed to load drivers list.');
      setSnackbarOpen(true);
      setDriversList([]);
      setSelectedDriver('');
    } finally {
      setDriversLoading(false); // Use driversLoading for this fetch
    }
  };

  fetchDrivers();
}, [isOrg1User, isDriverOnly, currentUser, selectedFacility, facilitiesList]);
// Add logging for driversList and selectedDriver updates
useEffect(() => {
  console.log('[Performance] driversList updated:', {
    length: driversList.length,
    ids: driversList.map(d => d._id)
  });
}, [driversList]);

useEffect(() => {
  console.log('[Performance] selectedDriver updated:', {
    value: selectedDriver,
    existsInList: driversList.some(d => d._id === selectedDriver)
  });
}, [selectedDriver, driversList]);

// Function to fetch performance data
const fetchPerformanceData = useCallback(async () => {
  // For Org 1 driver-only users, we need a driver ID
  // For other users, we need either a facility ID or a driver ID (if Org 1 with admin/dispatcher role)
  if (isDriverOnly && !selectedDriver) {
    setMetrics(null);
    setLoading(false);
    return;
  }
  
  if (!isDriverOnly && !selectedDriver && !selectedFacility) {
    // Don't fetch if neither facility nor driver is selected
    setMetrics(null);
    setLoading(false);
    return;
  }

  if (!startDate || !endDate) {
    // Don't fetch if dates are not set
    setMetrics(null);
    setLoading(false);
    return;
  }

  setLoading(true);
  setMetrics(null); // Clear previous metrics
  setReviews([]); // Clear previous reviews (if they were tied to metrics)

  try {
    const token = localStorage.getItem('token');
    const config = {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        // Format dates as YYYY-MM-DD for the API query parameters
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      },
    };

    let data;
    
    // Determine which API endpoint to use based on user role and selection
    if (selectedDriver && isOrg1User) {
      // Fetch driver performance metrics
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/driver-performance/${selectedDriver}`, config);
      data = response.data;
    } else if (selectedFacility) {
      // Fetch facility performance metrics
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/performance/${selectedFacility}`, config);
      data = response.data;
    } else {
      throw new Error('No valid selection for performance metrics');
    }

    // TODO: Process reviews if they are part of the response or fetched separately
    // For now, assuming reviews are not part of this specific API response
    setMetrics(data); // Assuming API returns the metrics object directly

  } catch (error) {
    console.error('Error fetching performance data:', error);
    setSnackbarMessage(`Failed to load performance data: ${error.response?.data?.message || error.message}`);
    setSnackbarOpen(true);
    setMetrics(null); // Ensure metrics are null on error
  } finally {
    setLoading(false);
  }
}, [selectedFacility, selectedDriver, startDate, endDate, isOrg1User, isDriverOnly]); // Dependencies for the fetch function

// Effect to trigger data fetch when facility, driver, or dates change
useEffect(() => {
  fetchPerformanceData();
}, [fetchPerformanceData]); // Dependency array includes the memoized fetch function

// Handle driver selection change
const handleDriverChange = (event) => {
  const driverId = event.target.value;
  setSelectedDriver(driverId);
  // The useEffect hook will now handle triggering the data fetch
};

// Toggle filters panel
const toggleFilters = () => {
  setShowFilters(!showFilters);
};

// Apply filters to facilities list
useEffect(() => {
  if (!facilitiesList.length) return;
  
  let filtered = [...facilitiesList];
  
  // Apply company name filter - this would need to be implemented with a backend call
  // For now, we'll just filter by facility name as a proxy
  if (companyNameFilter) {
    filtered = filtered.filter(facility =>
      facility.facilityName.toLowerCase().includes(companyNameFilter.toLowerCase())
    );
  }
  
  // Apply vendor ID filter
  if (vendorIdFilter) {
    filtered = filtered.filter(facility =>
      facility.vendorNumber && facility.vendorNumber.toLowerCase().includes(vendorIdFilter.toLowerCase())
    );
  }
  
  // Apply city filter - would need facility data to include city
  // This is a placeholder for when that data is available
  if (cityFilter) {
    filtered = filtered.filter(facility =>
      facility.city && facility.city.toLowerCase().includes(cityFilter.toLowerCase())
    );
  }
  
  // Apply state filter - would need facility data to include state
  if (stateFilter) {
    filtered = filtered.filter(facility =>
      facility.state && facility.state.toLowerCase().includes(stateFilter.toLowerCase())
    );
  }
  
  // Apply country filter - would need facility data to include country
  if (countryFilter) {
    filtered = filtered.filter(facility =>
      facility.country && facility.country.toLowerCase().includes(countryFilter.toLowerCase())
    );
  }
  
  // Apply zip filter - would need facility data to include zip
  if (zipFilter) {
    filtered = filtered.filter(facility =>
      facility.zip === zipFilter ||
      (facility.coveredZipCodes && facility.coveredZipCodes.includes(zipFilter))
    );
  }
  
  setFilteredFacilitiesList(filtered);
}, [facilitiesList, companyNameFilter, vendorIdFilter, cityFilter, stateFilter, countryFilter, zipFilter]);

// Reset all filters
const resetFilters = () => {
  setCompanyNameFilter('');
  setVendorIdFilter('');
  setCityFilter('');
  setStateFilter('');
  setCountryFilter('');
  setZipFilter('');
};

// REMOVED loadSampleData function
// REMOVED combineData function
  // REMOVED combineData function

  // Handle facility selection change
  const handleFacilityChange = (event) => {
    const facilityId = event.target.value; // Value is facility._id
    setSelectedDriver(''); // Reset selected driver
    setSelectedFacility(facilityId);
    // The useEffect hook will now handle triggering the data fetch
    // console.log("Selected Facility Vendor Number:", vendorNumber);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle review filter change
  const handleReviewFilterChange = (event) => {
    setReviewFilter(event.target.value);
  };

  // Filtered reviews
  const filteredReviews = reviews.filter((review) => {
    if (reviewFilter === 'All') return true;
    if (reviewFilter === 'Positive') return review.rating >= 8;
    if (reviewFilter === 'Neutral') return review.rating >= 5 && review.rating < 8;
    if (reviewFilter === 'Negative') return review.rating < 5;
    return true;
  });

  // Search reviews
  const searchedReviews = filteredReviews.filter((review) =>
    review.comment.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handlers for date range changes
  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    // The useEffect hook will now handle triggering the data fetch
    // console.log("Start Date Changed:", newValue.toISOString());
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    // The useEffect hook will now handle triggering the data fetch
    // console.log("End Date Changed:", newValue.toISOString());
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <>
      {/* Side Menu */}
      <SideMenu />

      {/* Main Content */}
      <Container maxWidth={false}>
        <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Performance</Typography>
          <Box display="flex" alignItems="center" data-tour="performance-controls">
            {/* Filter Toggle Button - Only show for Org 1 users with admin/dispatcher roles */}
            {isOrg1User && hasAdminOrDispatcherRole && (
              <Button
                variant="outlined"
                startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={toggleFilters}
                sx={{ mr: 2 }}
                data-tour="performance-filter-toggle"
              >
                Filters
              </Button>
            )}
            {/* Facility Filter - Only show for non-Org 1 users or Org 1 users with admin/dispatcher roles */}
            {showFacilityDropdown && (
              <FormControl sx={{ minWidth: 250, mr: 2 }} size="small">
                <InputLabel id="facility-select-label">Facility</InputLabel>
                <Select
                  labelId="facility-select-label"
                  id="facility-select"
                  value={selectedFacility}
                  label="Facility"
                  onChange={handleFacilityChange}
                  disabled={facilitiesList.length === 0} // Disable if list is empty
                >
                  {/* Add null option for facility dropdown */}
                  <MenuItem value="">-Select Facility-</MenuItem>
                  
                  {facilitiesList.length === 0 && (
                    <MenuItem value="" disabled>Loading facilities...</MenuItem>
                  )}
                  {(showFilters ? filteredFacilitiesList : facilitiesList).map((facility) => { // Use filtered list when filters are active
                    return ( // Add explicit return statement
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facilityName} ({facility.vendorNumber})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
            
            {/* Driver Filter - Only show for Org 1 users */}
            {showDriverDropdown && (
              <FormControl sx={{ minWidth: 250, mr: 2 }} size="small">
                <InputLabel id="driver-select-label">Driver</InputLabel>
{console.log('[Performance] Rendering Driver Select. driversList:', driversList, 'selectedDriver:', selectedDriver, 'driversLoading:', driversLoading, 'mainLoading:', loading)}
                <Select
                  labelId="driver-select-label"
                  id="driver-select"
                  value={selectedDriver}
                  label="Driver"
                  onChange={handleDriverChange}
                  disabled={driversLoading || (driversList.length === 0 && isDriverOnly)} // Use driversLoading
                  onOpen={() => {
                    console.log('[Performance] Driver Select opened:', {
                      value: selectedDriver,
                      options: driversList.map(d => d._id),
                      driversLoading // Log driversLoading
                    });
                  }}
                >
                  {/* Add null option for admin/dispatcher users */}
                  {hasAdminOrDispatcherRole && (
                    <MenuItem value="">-Select Driver-</MenuItem>
                  )}
                  
                  {driversLoading ? ( // Use driversLoading here
                    <MenuItem value="" disabled>Loading drivers...</MenuItem>
                  ) : driversList.length === 0 ? (
                    <MenuItem value="" disabled>No drivers found for this facility</MenuItem>
                  ) : (
                    driversList.map((driver) => (
                      <MenuItem key={driver._id} value={driver._id}>
                        {driver.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
            
            {/* Date Range Pickers */}
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              // Use slots prop instead of renderInput
              slots={{ textField: (params) => <TextField {...params} size="small" sx={{ mr: 1 }} data-tour="performance-dates" /> }}
              maxDate={endDate} // Prevent start date being after end date
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              // Use slots prop instead of renderInput
              slots={{ textField: (params) => <TextField {...params} size="small" /> }}
              minDate={startDate} // Prevent end date being before start date
              maxDate={dayjs()} // Prevent selecting future dates
            />
          </Box>
        </Box>
        
        {/* Filters Panel - Only show when filters are toggled on */}
        {showFilters && isOrg1User && hasAdminOrDispatcherRole && (
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Advanced Filters</Typography>
              <Button variant="outlined" size="small" onClick={resetFilters}>Reset Filters</Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="Company Name"
                  value={companyNameFilter}
                  onChange={(e) => setCompanyNameFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="Vendor ID/Number"
                  value={vendorIdFilter}
                  onChange={(e) => setVendorIdFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="City"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="State"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="Country"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="Zip Code"
                  value={zipFilter}
                  onChange={(e) => setZipFilter(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        )}
        
        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
        {!loading && metrics && (
          <Grid container spacing={4} data-tour="performance-metrics">
            {/* Performance Metrics */}
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Customer Rates"
                tooltip="Average rating provided by customers (1-10)"
                icon={<StarRateIcon />}
                color="#f9a825"
                value={metrics.customerRates != null ? metrics.customerRates.toFixed(1) : 'N/A'}
              />
            </Grid>

            {/* SMS Customer Ratings - Provider Rating */}
            {metrics.smsCustomerRatings && (
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="SMS Provider Rating"
                  tooltip="Average provider rating from SMS feedback (1-10)"
                  icon={<SmsOutlinedIcon />}
                  color="#7b1fa2"
                  value={metrics.smsCustomerRatings.avgProviderRating != null ?
                    metrics.smsCustomerRatings.avgProviderRating.toFixed(1) : 'N/A'}
                  extra={
                    <Typography variant="body2" color="textSecondary">
                      Based on {metrics.smsCustomerRatings.totalRatings || 0} SMS responses
                    </Typography>
                  }
                />
              </Grid>
            )}

            {/* SMS Customer Ratings - Answering Service Rating */}
            {metrics.smsCustomerRatings && (
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="SMS Answering Service"
                  tooltip="Average answering service rating from SMS feedback (1-10)"
                  icon={<SupportAgentIcon />}
                  color="#00838f"
                  value={metrics.smsCustomerRatings.avgAnsweringServiceRating != null ?
                    metrics.smsCustomerRatings.avgAnsweringServiceRating.toFixed(1) : 'N/A'}
                  extra={
                    <Typography variant="body2" color="textSecondary">
                      Phone system experience
                    </Typography>
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Completion Rate"
                tooltip="Percentage of successfully completed calls"
                icon={<TaskAltIcon />}
                color="#2e7d32"
                value={metrics.completionRate != null ? `${metrics.completionRate.toFixed(1)}%` : 'N/A'}
                percent={metrics.completionRate}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Acceptance Rate"
                tooltip="Accepted offers (including ETAs submitted) out of all responded dispatch offers. Manual and automatic rejections both count against this rate."
                icon={<ThumbUpAltIcon />}
                color="#1565c0"
                value={metrics.acceptanceRate != null ? `${metrics.acceptanceRate.toFixed(1)}%` : 'N/A'}
                percent={metrics.acceptanceRate}
                extra={metrics.acceptanceStats && metrics.acceptanceStats.total > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" color="success" variant="outlined"
                      label={`${metrics.acceptanceStats.accepted} accepted`} />
                    <Chip size="small" color="error" variant="outlined"
                      label={`${metrics.acceptanceStats.rejected} rejected`} />
                    <Chip size="small" color="warning" variant="outlined"
                      label={`${metrics.acceptanceStats.autoRejected} timed out`} />
                  </Stack>
                ) : null}
              />
            </Grid>
            {/* Additional Metrics */}
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Location Service"
                tooltip="Percentage of time drivers were traceable via GPS"
                icon={<GpsFixedIcon />}
                color="#5d4037"
                value={metrics.locationService != null ? `${metrics.locationService.toFixed(1)}%` : 'N/A'}
                percent={metrics.locationService}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Photo Capture"
                tooltip="Percentage of calls with photos uploaded"
                icon={<PhotoCameraIcon />}
                color="#455a64"
                value={(metrics.photoCapture === null || typeof metrics.photoCapture === 'undefined') ? 'N/A' : `${metrics.photoCapture.toFixed(1)}%`}
                percent={typeof metrics.photoCapture === 'number' ? metrics.photoCapture : null}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Total Jobs"
                tooltip="Number of calls successfully serviced"
                icon={<WorkHistoryIcon />}
                color="#283593"
                value={metrics.totalJobs != null ? metrics.totalJobs : 'N/A'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Total Earnings"
                tooltip="Total dollar amount earned"
                icon={<PaidIcon />}
                color="#1b5e20"
                value={metrics.totalEarnings != null ? `$${metrics.totalEarnings.toFixed(2)}` : 'N/A'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Average ETA"
                tooltip="Average estimated time of arrival provided by drivers"
                icon={<ScheduleIcon />}
                color="#0277bd"
                value={metrics.averageEta != null ? `${metrics.averageEta.toFixed(1)} mins` : 'N/A'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Average ATA"
                tooltip="Average arrival delta vs the accepted ETA (positive = late, negative = early)"
                icon={<SportsScoreIcon />}
                color="#c62828"
                value={metrics.averageAta != null ? `${metrics.averageAta.toFixed(1)} mins` : 'N/A'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="On-Time Arrival %"
                tooltip={metrics.onTimeStats
                  ? `Arrivals within 15 min of the accepted ETA. Early: ${metrics.onTimeStats.early} | On-Time: ${metrics.onTimeStats.onTime} | Grace: ${metrics.onTimeStats.grace} | Late: ${metrics.onTimeStats.late}${metrics.onTimeStats.bonusEarned ? ` | Bonuses earned: $${metrics.onTimeStats.bonusEarned.toFixed(2)}` : ''}`
                  : 'Share of arrivals within 15 minutes of the accepted ETA'}
                icon={<ScheduleIcon />}
                color="#2e7d32"
                value={metrics.onTimePercent != null ? `${metrics.onTimePercent.toFixed(1)}%` : 'N/A'}
              />
            </Grid>
            {/* Service Type Breakdown */}
            <Grid item xs={12} md={6}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6">Service Type Breakdown</Typography>
                  {metrics.serviceTypeBreakdown && Object.keys(metrics.serviceTypeBreakdown).length > 0 ? (
                    <Pie
                      data={{
                        labels: Object.keys(metrics.serviceTypeBreakdown),
                        datasets: [
                          {
                            data: Object.values(metrics.serviceTypeBreakdown),
                            backgroundColor: [
                              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
                              '#FF9F40', '#FFCD56', '#C9CBCF', '#4D5360', '#AC64AD' // Add more colors if needed
                            ],
                          },
                        ],
                      }}
                      options={{
                        plugins: {
                          tooltip: {
                            callbacks: {
                              label: (tooltipItem) => {
                                const label = tooltipItem.label || '';
                                const value = tooltipItem.raw || 0;
                                return `${label}: ${value.toFixed(1)}%`; // Format as percentage
                              },
                            },
                            },
                          legend: {
                             display: true, // Optionally display legend
                             position: 'bottom',
                          },
                        },
                      }}
                    />
                  ) : (
                    <Typography sx={{ textAlign: 'center', mt: 4 }}>No service data available.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {/* SMS Rating Distribution Chart */}
            {metrics.smsCustomerRatings && metrics.smsCustomerRatings.totalRatings > 0 && (
              <Grid item xs={12} md={6}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography variant="h6">Provider Rating Distribution</Typography>
                    <Pie
                      data={{
                        labels: ['Poor (1-3)', 'Fair (4-6)', 'Good (7-8)', 'Excellent (9-10)'],
                        datasets: [
                          {
                            data: [
                              metrics.smsCustomerRatings.providerRatingDistribution?.poor || 0,
                              metrics.smsCustomerRatings.providerRatingDistribution?.fair || 0,
                              metrics.smsCustomerRatings.providerRatingDistribution?.good || 0,
                              metrics.smsCustomerRatings.providerRatingDistribution?.excellent || 0
                            ],
                            backgroundColor: [
                              '#f44336', // Red for poor
                              '#ff9800', // Orange for fair
                              '#4caf50', // Green for good
                              '#2196f3'  // Blue for excellent
                            ],
                          },
                        ],
                      }}
                      options={{
                        plugins: {
                          tooltip: {
                            callbacks: {
                              label: (tooltipItem) => {
                                const label = tooltipItem.label || '';
                                const value = tooltipItem.raw || 0;
                                const total = metrics.smsCustomerRatings.totalRatings;
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                return `${label}: ${value} (${percentage}%)`;
                              },
                            },
                          },
                          legend: {
                            display: true,
                            position: 'bottom',
                          },
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Recent SMS Ratings */}
            {metrics.smsCustomerRatings && metrics.smsCustomerRatings.recentRatings && metrics.smsCustomerRatings.recentRatings.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography variant="h6">Recent SMS Ratings (Last 30 Days)</Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 2 }}>
                      {metrics.smsCustomerRatings.recentRatings.slice(0, 10).map((rating, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee' }}>
                          <Box>
                            <Typography variant="body2">
                              Provider: {rating.providerRating}/10 | Service: {rating.answeringServiceRating}/10
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(rating.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Performance Over Time - REMOVED - Data not provided by current API */}
            {/* Earnings Over Time - REMOVED - Data not provided by current API */}

            {/* Customer Reviews - REMOVED - Data not provided by current API */}
          </Grid>
        )}
      </Container>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </>
   </LocalizationProvider>
  );
}

export default Performance;
